import { Box, Divider, SkeletonRectangle, Text } from '@wix/design-system';
import {
  CycleUtils,
  getSearchParams,
  GtmEvents,
  useGtmLogger,
} from '@wix/package-picker-shared';
import {
  NonBlockerWrapper,
  PageDisclaimer,
  TrustBuilders,
  useViewChange,
  EclipsePackagePickerSteps,
  EclipseCta,
  useAnonymousCheckoutRedirect,
  useInitLayoutBiEvents,
  useInitSelectCyclePageBiEvents,
  useIsTrustBuildersRedesign,
} from '@wix/package-picker-shared-eclipse';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import React from 'react';
import { CycleSelector } from '../CycleSelector';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { AllPlansIncludedFeaturesBanner } from '../AllPlansIncludedFeaturesBanner';
import {
  StepSubtitle,
  CONTEXTUAL_SUBTITLE_EXPERIMENT_KEY,
} from '../StepSubtitle';
import { NavigateBack } from '../NavigateBack';
import { PageTitle as DefaultPageTitle } from '../PageTitle';

export interface CycleSectionProps {
  /** Custom page title. If not provided, the default PageTitle component is used. */
  PageTitle?: React.ReactNode;
  /** Custom disclaimer. If not provided, the default PageDisclaimer component is used. */
  Disclaimer?: React.ReactNode;
  /** Whether to show the TrustBuilders section. Defaults to true. */
  showTrustBuilders?: boolean;
  /** Whether to show the back button. Defaults to true. */
  showBackButton?: boolean;
  /** Custom back button handler. If not provided, defaults to navigating back to select-plan. */
  onBack?: () => void;
  /**
   * Custom CTA button content. If not provided, defaults to the "Continue to checkout" translation.
   * Accepts a string or any React node (e.g. an icon + text).
   */
  ctaText?: React.ReactNode;
  /**
   * Custom handler for the primary CTA button.
   * Receives the selected `cycle` object so the consumer can decide what to do next.
   * When provided, replaces the default checkout flow entirely
   * (no anonymous redirect, no site authorization checks, no modal — just your handler).
   * The default BI event `onContinueToCheckoutClick` is still fired.
   */
  onContinue?: (cycle: PurchaseOffering.PurchaseCycle) => void;
  /** Custom feature names for the "all plans included" banner. Overrides the API-fetched list. */
  allPlansIncludedFeatures?: string[];
}

export const CycleSection = ({
  PageTitle,
  Disclaimer,
  showTrustBuilders = true,
  showBackButton = true,
  onBack,
  ctaText,
  onContinue,
  allPlansIncludedFeatures,
}: CycleSectionProps) => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const isTrustBuildersRedesign = useIsTrustBuildersRedesign();
  const { experiments, ready: experimentsReady } =
    EclipsePackagePickerModule.internals.experiments.hooks.useExperiments();
  const isMatchDomainCycleEnabled = experiments.isEnabled(
    'matchDomainCycleToPremiumCycle',
  );
  const isStepSubtitleEnabled = experiments.isEnabled(
    CONTEXTUAL_SUBTITLE_EXPERIMENT_KEY,
  );
  const isDesktopChangeContinueCtaEnabled = experiments.isEnabled(
    'specs.premium.desktopChangeContinueCTA',
  );
  const defaultCtaKey = isDesktopChangeContinueCtaEnabled
    ? 'subscription_btn.continue'
    : 'subscription_btn.continue_to_checkout';
  const handleViewChange = useViewChange();
  const { isAnonymous, redirectToCheckoutWithSignIn } =
    useAnonymousCheckoutRedirect();
  const didReportLoad = React.useRef(false);
  useGtmLogger(GtmEvents.SELECT_CYCLE);
  const { didInit } = useInitSelectCyclePageBiEvents();
  useInitLayoutBiEvents();
  const selectedCycleId =
    EclipsePackagePickerModule.SDK.PackagePickerStore.useSelectedCycleId();
  const {
    siteGuid,
    productId: urlProductId,
    cycleId: urlCycleId,
  } = getSearchParams();

  // Sync productId & cycleId from URL query params into store
  React.useEffect(() => {
    if (urlProductId) {
      EclipsePackagePickerModule.SDK.PackagePickerStore.setSelectedProductId(
        urlProductId,
      );
    }
    if (urlCycleId) {
      EclipsePackagePickerModule.SDK.PackagePickerStore.setSelectedCycleId(
        CycleUtils.validateCycleId(urlCycleId),
      );
    }
  }, []);

  React.useEffect(() => {
    if (didInit && !didReportLoad.current) {
      EclipsePackagePickerModule.internals.bi.onSelectCyclePageLoad?.({});
      didReportLoad.current = true;
    }
  }, [didInit]);

  const handleContinueToCheckout = () => {
    EclipsePackagePickerModule.internals.bi.onContinueToCheckoutClick?.({});

    if (onContinue && selectedCycleId) {
      const cycle = CycleUtils.convertCycleIdToCycle(selectedCycleId);
      if (cycle) {
        onContinue(cycle);
        return;
      }
    }

    if (isAnonymous) {
      redirectToCheckoutWithSignIn();
      return;
    }

    if (!siteGuid) {
      EclipsePackagePickerModule.internals.modalsController.NoSitesModal.show(
        {},
      );
      return;
    }

    if (selectedCycleId) {
      handleViewChange(EclipsePackagePickerSteps.CHECKOUT, {
        cycleId: selectedCycleId,
      });
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    EclipsePackagePickerModule.internals.bi.onBackButtonClick?.({
      fromStep: EclipsePackagePickerSteps.SELECT_CYCLE,
    });
    handleViewChange(EclipsePackagePickerSteps.SELECT_PLAN, {
      cycleId: null,
    });
  };

  return (
    <>
      <Box width="100%" direction="horizontal" verticalAlign="middle">
        {showBackButton && (
          <NavigateBack
            onBack={handleBack}
            backButtonText={t('premium-purchase-plan.eclipse.back.cta')}
          />
        )}
        {PageTitle !== undefined ? PageTitle : <DefaultPageTitle />}
      </Box>
      <Box width="100%" marginTop="SP3">
        {isStepSubtitleEnabled ? (
          <StepSubtitle textKey="premium-purchase-plan.eclipse.select_cycle.subtitle" />
        ) : isMatchDomainCycleEnabled ? (
          <Box width="100%" align="center">
            <Text size="medium">
              {t('premium-purchase-plan.subtitle.domain-match-cycle')}
            </Text>
          </Box>
        ) : (
          <NonBlockerWrapper componentName="AllPlansIncludedFeaturesBanner">
            <AllPlansIncludedFeaturesBanner
              features={allPlansIncludedFeatures}
            />
          </NonBlockerWrapper>
        )}
      </Box>
      <Box width="100%" align="center" direction="vertical">
        <Box
          width="100%"
          maxWidth="720px"
          direction="vertical"
          margin="SP7 SP3 SP5 SP3"
          gap="SP5"
        >
          <CycleSelector />
          <Box align="right">
            {experimentsReady ? (
              <EclipseCta
                dataHook="select-cycle-cta"
                onClick={handleContinueToCheckout}
                disabled={!selectedCycleId}
                variant="primary"
              >
                {ctaText ?? t(defaultCtaKey)}
              </EclipseCta>
            ) : (
              <SkeletonRectangle height="48px" width="220px" />
            )}
          </Box>
        </Box>
        {isTrustBuildersRedesign ? (
          <Box direction="vertical" gap="SP5" width="100%" align="center">
            <Box
              width="100%"
              maxWidth="1000px"
              padding="0 SP2"
              boxSizing="border-box"
            >
              {Disclaimer !== undefined ? Disclaimer : <PageDisclaimer />}
            </Box>
            <Box
              direction="vertical"
              gap="50px"
              width="100%"
              maxWidth="1280px"
              padding="0 SP2"
              boxSizing="border-box"
            >
              <Divider />
              {showTrustBuilders && <TrustBuilders />}
            </Box>
          </Box>
        ) : (
          <Box direction="vertical" gap="SP5" margin="0 SP2">
            {Disclaimer !== undefined ? Disclaimer : <PageDisclaimer />}
            <Divider />
            {showTrustBuilders && <TrustBuilders />}
          </Box>
        )}
      </Box>
    </>
  );
};
